import type { ScheduleMember } from '@nucleus-commerce/api-client/admin';

/** The member's most recent time live in any target store, or null when it never has been. */
export function lastLiveAt(member: Pick<ScheduleMember, 'placements'>): string | null {
  let latest: string | null = null;
  for (const placement of member.placements) {
    if (placement.lastLiveAt && (latest === null || placement.lastLiveAt > latest)) {
      latest = placement.lastLiveAt;
    }
  }
  return latest;
}

/** Can a run put it live: not pinned (always live), not excluded, an active master product. */
const rotates = (member: ScheduleMember) =>
  !member.pinned &&
  !member.excluded &&
  !member.product.deleted &&
  member.product.status === 'active';

/**
 * The members most likely in the next batch, in the order a run picks them: never live first,
 * then least recently live, then position, then product id; `liveCount` of them. It reads one
 * clock across stores where a run reads each store's own, so it is a hint, not the preview.
 */
export function nextUpIds(members: readonly ScheduleMember[], liveCount: number): Set<string> {
  const ranked = members
    .filter(rotates)
    .map((member) => ({ member, last: lastLiveAt(member) }))
    .sort((a, b) => {
      if (a.last !== b.last) {
        if (a.last === null) return -1;
        if (b.last === null) return 1;
        return a.last < b.last ? -1 : 1;
      }
      return (
        a.member.position - b.member.position ||
        a.member.productId.localeCompare(b.member.productId)
      );
    });
  return new Set(ranked.slice(0, Math.max(0, liveCount)).map(({ member }) => member.productId));
}
